import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import DownloadIcon from "@mui/icons-material/Download";
import {
  BASE_URL,
  getAllASnStatusData,
  fetchBoxNumberByVendor,
  uploadBillDetailsAPI,
  fetchVendorAsnApprovedList,
  fetchAppointmentDateQuantityMasterList,
  fetchAsnDocumentsList,
  createAsnAppointmentAPI,
  deleteASN,
  deleteBoxNumber,
} from "Service/ApiService";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Button, IconButton, TextField, Tooltip } from "@mui/material";
import { useLoading } from "context/LoadingContext";
import { useAlert } from "../context/AlertContext";
import PropTypes from "prop-types";
import StatusCell from "./StatusCell";
import MDTypography from "components/MDTypography";
import {
  CalendarMonth,
  Description,
  Edit,
  FactCheck,
  Inventory2,
  ReceiptLong,
  RemoveRedEye,
  Delete
} from "@mui/icons-material";
import MDInput from "components/MDInput";

// Define validation schema
const schema = Yup.object().shape({
  billNumber: Yup.string().required("Field is required"),
  invoiceDate: Yup.string().required("Field is required"),
  ewayBillNumber: Yup.string().required("Field is required"),
  deliveryDate: Yup.string().required("Field is required"),
});

const STATUS_DOCUMENTS = [
  {
    label: "Self-Inspection Quality Checklist",
    type: "self_inspection_quality_checklist",
    iconName: "fact_check",
  },
  {
    label: "Packing Slip",
    type: "packing_slip",
    iconName: "inventory",
  },
  {
    label: "Invoice Copy",
    type: "invoice_copy",
    iconName: "receipt",
  },
];

const ASN_DOCUMENT_BASE_URL = `${BASE_URL}vendor/uploads/asn_documents/`;

const AsnStatusController = () => {
  const [asnStatus, setASNSTatusData] = useState([]);
  // Complete (unfiltered, unpaginated) ASN list used ONLY by the Appointment
  // dialog so it always shows every eligible ASN regardless of the main
  // table's PO search filter / pagination. Fetched via the same
  // getAllASnStatusData API, just without filters.
  const [appointmentAsnStatus, setAppointmentAsnStatus] = useState([]);
  const { setLoading } = useLoading();
  const { showAlert } = useAlert();
  const [openPopup, setOpenPopup] = useState(false);
  const [openEditPopup, setOpenEditPopup] = useState(false);
  const [isBillDetailsViewMode, setIsBillDetailsViewMode] = useState(false);
  const [isBillDetailsUpdateMode, setIsBillDetailsUpdateMode] = useState(false);
  const [openAppointmentPopup, setOpenAppointmentPopup] = useState(false);
  const [openBookingPreviewPopup, setOpenBookingPreviewPopup] = useState(false);
  const [openAppointmentListPopup, setOpenAppointmentListPopup] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [selectedAppointmentData, setSelectedAppointmentData] = useState(null);
  const [selectedAppointmentListData, setSelectedAppointmentListData] = useState(null);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState("");
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState(new Date());
  const [selectedEwayBillFile, setSelectedEwayBillFile] = useState(null);
  const [approvedAsnRows, setApprovedAsnRows] = useState([]);
  const [bookingPreviewLoading, setBookingPreviewLoading] = useState(false);
  const [asnStatusLoading, setAsnStatusLoading] = useState(true);
  const [selectedApprovedAsnIds, setSelectedApprovedAsnIds] = useState([]);
  const [appointmentDateSlotMap, setAppointmentDateSlotMap] = useState({});
  const [bookingSelectionConfirmed, setBookingSelectionConfirmed] = useState(false);
  const [confirmedBookingQuantity, setConfirmedBookingQuantity] = useState(0);
  const [asnDocumentsMap, setAsnDocumentsMap] = useState({});
  const [approvedAsnAvailabilityMap, setApprovedAsnAvailabilityMap] = useState({});
  const [approvedAsnAvailabilityLoading, setApprovedAsnAvailabilityLoading] = useState(false);

  const [searchPONumber, setSearchPONumber] = useState("");
  const [poFilter, setPoFilter] = useState("");

  const [editPONumber, setEditPONumber] = useState("");
  const [editData, setEditData] = useState([]);

  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [deleteData, setDeleteData] = useState(null);

  const [openDeleteBoxPopup, setOpenDeleteBoxPopup] = useState(false);
  const [selectedDeleteBox, setSelectedDeleteBox] = useState(null);

  // ---- Multi ASN Appointment Booking state ----
  const [isMultiAsnMode, setIsMultiAsnMode] = useState(false); // opened via top-right "Create Appointment"
  const [selectedASNMap, setSelectedASNMap] = useState({}); // { [asnKey]: true/false }
  const [materialSelectionMap, setMaterialSelectionMap] = useState({}); // { [asnKey]: { [materialId]: true } }
  const [approvedMaterialCache, setApprovedMaterialCache] = useState({}); // { [asnKey]: mappedRows[] }
  const [approvedMaterialLoadingMap, setApprovedMaterialLoadingMap] = useState({}); // { [asnKey]: true/false }
  const [expandedAccordions, setExpandedAccordions] = useState([]); // [asnKey, ...] -> now holds the single "active" PO key shown in the right-hand materials panel
  const [appointmentTabIndex, setAppointmentTabIndex] = useState(0); // 0 = Select Date, 1 = Select PO & Materials

  // pagination
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [rowCount, setRowCount] = useState(0);
  const vendordetails = JSON.parse(localStorage.getItem("vendor"));
  const vendorCode = vendordetails ? vendordetails.vendorCode : null;

  const [billData, setBillData] = useState([]);
  const [errors, setErrors] = useState({});
  const billSchema = Yup.object().shape({
    boxNumber: Yup.string().required(),
    billNumber: Yup.string().required("Invoice Number is required"),
    invoiceDate: Yup.string().required("Invoice Date is required"),
    ewayBillNumber: Yup.string().required("E-Way Bill Number is required"),
    deliveryDate: Yup.string().required("Delivery Date is required"),
  });

  const [deleteLoading, setDeleteLoading] = useState(false);

  // Validation
  const validateField = async (boxNumber, fieldName, value) => {
    try {
      await Yup.reach(schema, fieldName).validate(value);

      setErrors((prev) => ({
        ...prev,
        [boxNumber]: {
          ...prev[boxNumber],
          [fieldName]: null, // clear error if valid
        },
      }));
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        [boxNumber]: {
          ...prev[boxNumber],
          [fieldName]: err.message, // set error for this box + field
        },
      }));
    }
  };

  useEffect(() => {
    getVendorStatus();
  }, [paginationModel, searchPONumber]);

  useEffect(() => {
    if (vendorCode) {
      getAppointmentDateSlots();
      getAppointmentAsnList();
    }
  }, [vendorCode]);

  const handleDeleteBox = async (row) => {
    try {
      setDeleteLoading(true);

      const response = await deleteBoxNumber(
        row.boxNumber,
        row.vendorCode,
        row.poNumber
      );

      if (response?.success === "true" || response?.success === true) {
        showAlert("Box deleted successfully.", {
          icon: "success",
          title: "Success",
        });
        handleClosePopup();
        getVendorStatus();
      } else {
        showAlert(response?.msg || "Unable to delete box.", {
          icon: "error",
          title: "Error",
        });
      }
    } catch (err) {
      console.error(err);
      showAlert("Unable to delete box.", {
        icon: "error",
        title: "Error",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const getVendorStatus = async () => {
    try {
      setAsnStatusLoading(true);
      setASNSTatusData([]);
      const response = await getAllASnStatusData(
        vendorCode,
        searchPONumber,
        paginationModel.page,
        paginationModel.pageSize
      ); // Assuming this function fetches orders data from the API
      console.log("ASN Status Response:", response);
      if (response && response.success === "true") {
        setASNSTatusData(response.ASNStatusList);
        setRowCount(response.rowCount);
        loadAsnDocuments(response.ASNStatusList || []);
        loadApprovedAsnAvailability(response.ASNStatusList || []);
      } else {
        setAsnDocumentsMap({});
        setApprovedAsnAvailabilityMap({});
      }
    } catch (error) {
      setAsnDocumentsMap({});
      setApprovedAsnAvailabilityMap({});
    } finally {
      setAsnStatusLoading(false);
    }
  };

  // Fetches the COMPLETE ASN list (no PO search filter, no pagination) using
  // the same getAllASnStatusData API. This is used exclusively by the
  // Appointment dialog so it is never affected by the main table's search
  // filter or current page.
  const getAppointmentAsnList = async () => {
    try {
      const response = await getAllASnStatusData(vendorCode, "", 0, 10000);
      if (response && response.success === "true") {
        setAppointmentAsnStatus(response.ASNStatusList || []);
      } else {
        setAppointmentAsnStatus([]);
      }
    } catch (error) {
      setAppointmentAsnStatus([]);
    }
  };

  const handleDeleteClick = (row) => {
    const boxNumbers = [...new Set(row.items.map((item) => item.boxNumber))];

    setDeleteData({
      ...row,
      boxNumbers,
    });

    setOpenDeletePopup(true);
  };

  const handleOpenDeleteBoxPopup = (row) => {
    setSelectedDeleteBox(row);
    setOpenDeleteBoxPopup(true);
  };

  const handleCloseDeleteBoxPopup = () => {
    setOpenDeleteBoxPopup(false);
    setSelectedDeleteBox(null);
  };

  const handleCloseDeletePopup = () => {
    setOpenDeletePopup(false);
    setDeleteData(null);
  };

  const handleDeleteASN = async () => {
    try {
      setLoading(true);

      const response = await deleteASN({
        vendorCode,
        poNumber: deleteData.poNumber,
        boxNumbers: deleteData.boxNumbers, // array
      });

      if (response.success === "true") {
        await Promise.all([getVendorStatus(), getAppointmentAsnList()]);

        showAlert(response.msg || "ASN deleted successfully", {
          icon: "success",
          title: "Success",
        });

        handleCloseDeletePopup();
      } else {
        showAlert(response.msg, {
          icon: "error",
          title: "Error",
        });
      }
    } catch (error) {
      showAlert("Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  const DownloadButtonCell = ({ row }) => (
    <IconButton onClick={() => hangetstagesgroups.phpdleDownloadClick(row)} color="success">
      <DownloadIcon />
    </IconButton>
  );

  DownloadButtonCell.propTypes = {
    row: PropTypes.object.isRequired,
  };

  const formatDateValue = (value) => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const parseGridDateValue = (value) => {
    if (!value) return null;

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === "string") {
      const trimmedValue = value.trim();
      const ddmmyyyyMatch = trimmedValue.match(/^(\d{2})-(\d{2})-(\d{4})$/);

      if (ddmmyyyyMatch) {
        const [, day, month, year] = ddmmyyyyMatch;
        const parsedDate = new Date(`${year}-${month}-${day}T00:00:00`);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
      }

      const parsedDate = new Date(trimmedValue);
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    return null;
  };

  const getMonthLabel = (dateValue) =>
    dateValue.toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });

  const normalizeDateKey = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
      date.getDate()
    ).padStart(2, "0")}`;
  };

  const todayDateKey = normalizeDateKey(new Date());

  const isAppointmentTakenEnabled = (item) => {
    const appointmentTakenValue =
      item?.isAppointmentTaken ?? item?.is_appointment_taken ?? item?.appointmentTaken;

    if (typeof appointmentTakenValue === "string") {
      return appointmentTakenValue.trim().toLowerCase() === "yes";
    }

    return appointmentTakenValue === true || appointmentTakenValue === 1;
  };

  const isPastDate = (dateKey) => {
    if (!dateKey) {
      return false;
    }

    return dateKey < todayDateKey;
  };

  const isCurrentOrFutureMonth = (monthDate) => {
    const today = new Date();
    const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const selectedMonthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);

    return selectedMonthStart >= currentMonthStart;
  };

  const getAppointmentDate = (item) =>
    item?.appointmentDate ||
    item?.appointment_date ||
    item?.appointmentBookedDate ||
    item?.appointment_booked_date ||
    item?.dispatchAppointmentDate ||
    item?.dispatch_appointment_date ||
    "";

  const buildDocumentLinks = (poNumber, vendorCode) => {
    if (!poNumber || !vendorCode) {
      return [];
    }

    return asnDocumentsMap[`${vendorCode}-${poNumber}`] || [];
  };

  const handleOpenDocument = (documentLink) => {
    if (!documentLink?.url) {
      return;
    }

    const link = document.createElement("a");

    link.href = documentLink.url;
    link.download = documentLink.fileName || "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const mapAsnDocumentLinks = (documentData) => {
    if (!documentData) {
      return [];
    }

    const documents = [
      {
        fileName: documentData.docment1Name,
        type: documentData.document1Type,
        iconName: "fact_check",
        label: "Self-Inspection Quality Checklist",
      },
      {
        fileName: documentData.document2Name,
        type: documentData.document2Type,
        iconName: "inventory",
        label: "Packing Slip",
      },
      {
        fileName: documentData.document3Name,
        type: documentData.document3Type,
        iconName: "receipt",
        label: "Invoice Copy",
      },
    ];

    return documents
      .filter((item) => item.fileName)
      .map((item) => ({
        ...item,
        url: `${ASN_DOCUMENT_BASE_URL}${encodeURIComponent(item.fileName)}`,
      }));
  };

  const loadAsnDocuments = async (asnList = []) => {
    const uniqueRows = Array.from(
      new Map(
        asnList
          .filter((item) => item?.vendorCode && item?.poNumber)
          .map((item) => [`${item.vendorCode}-${item.poNumber}`, item])
      ).values()
    );

    if (!uniqueRows.length) {
      setAsnDocumentsMap({});
      return;
    }

    try {
      const responses = await Promise.allSettled(
        uniqueRows.map((item) => fetchAsnDocumentsList(item.vendorCode, item.poNumber))
      );

      const nextDocumentMap = {};

      responses.forEach((result, index) => {
        const item = uniqueRows[index];
        const mapKey = `${item.vendorCode}-${item.poNumber}`;

        if (result.status === "fulfilled" && result.value?.success === "true") {
          nextDocumentMap[mapKey] = mapAsnDocumentLinks(result.value.asnDocumentList?.[0]);
        } else {
          nextDocumentMap[mapKey] = [];
        }
      });

      setAsnDocumentsMap(nextDocumentMap);
    } catch (error) {
      setAsnDocumentsMap({});
    }
  };

  const loadApprovedAsnAvailability = async (asnList = []) => {
    const uniqueRows = Array.from(
      new Map(
        asnList
          .filter((item) => item?.vendorCode && item?.poNumber)
          .map((item) => [`${item.vendorCode}-${item.poNumber}`, item])
      ).values()
    );

    if (!uniqueRows.length) {
      setApprovedAsnAvailabilityMap({});
      return;
    }

    try {
      setApprovedAsnAvailabilityLoading(true);
      const responses = await Promise.allSettled(
        uniqueRows.map((item) => fetchVendorAsnApprovedList(item.vendorCode, item.poNumber))
      );

      const nextAvailabilityMap = {};

      responses.forEach((result, index) => {
        const item = uniqueRows[index];
        const mapKey = `${item.vendorCode}-${item.poNumber}`;
        const response = result.status === "fulfilled" ? result.value : null;
        const hasMaterialDetails =
          response?.success === "true" && Array.isArray(response?.ASNStatusList)
            ? response.ASNStatusList.length > 0
            : false;

        nextAvailabilityMap[mapKey] = {
          hasMaterialDetails,
          message: hasMaterialDetails
            ? ""
            : response?.msg || "No material details found",
        };
      });

      setApprovedAsnAvailabilityMap(nextAvailabilityMap);
    } catch (error) {
      setApprovedAsnAvailabilityMap({});
    } finally {
      setApprovedAsnAvailabilityLoading(false);
    }
  };

  const buildAppointmentCalendarData = (dataList) => {
    const groupedByDate = {};

    dataList.forEach((item) => {
      const normalizedDate = normalizeDateKey(getAppointmentDate(item));
      if (!normalizedDate) {
        return;
      }

      if (!groupedByDate[normalizedDate]) {
        groupedByDate[normalizedDate] = {
          date: normalizedDate,
          totalQuantity: 0,
          bookings: [],
        };
      }

      groupedByDate[normalizedDate].totalQuantity += Number(item.quantity || 0);
      groupedByDate[normalizedDate].bookings.push({
        poNumber: item.poNumber,
        vendorCode: item.vendorCode,
        city: item.city,
        quantity: Number(item.quantity || 0),
        boxNumber: item.boxNumber,
        materialCode: item.materialCode,
        barcode: item.barcode,
      });
    });

    return groupedByDate;
  };

  const getCalendarDays = (monthDate) => {
    const firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const lastDay = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
    const startDay = firstDay.getDay();
    const totalDays = lastDay.getDate();
    const days = [];

    for (let i = 0; i < startDay; i += 1) {
      days.push(null);
    }

    for (let day = 1; day <= totalDays; day += 1) {
      const dateValue = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
      days.push({
        day,
        dateKey: normalizeDateKey(dateValue),
      });
    }

    return days;
  };

  const openAppointmentModal = async (row) => {
    setIsMultiAsnMode(false);
    setSelectedAppointmentData(row);
    setSelectedCalendarDate("");
    setSelectedEwayBillFile(null);
    setBookingSelectionConfirmed(false);
    setConfirmedBookingQuantity(0);
    setApprovedAsnRows([]);
    setSelectedApprovedAsnIds([]);
    setAppointmentTabIndex(0);
    resetMultiAsnSelectionState();
    setCurrentCalendarMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

    try {
      setLoading(true);
      await Promise.all([getAppointmentDateSlots(), getAppointmentAsnList()]);
    } finally {
      setLoading(false);
      setOpenAppointmentPopup(true);
      // Pre-select this ASN's PO in the "Select ASN & Materials" left panel
      // so the row-level icon keeps its original single-ASN feel, without removing
      // the option to pick other eligible ASNs too.
      setExpandedAccordions([getAsnKey(row)]);
      ensureApprovedMaterialsLoaded(row);
    }
  };

  const handleCloseAppointmentPopup = () => {
    setOpenAppointmentPopup(false);
    setOpenBookingPreviewPopup(false);
    setIsMultiAsnMode(false);
    setSelectedAppointmentData(null);
    setSelectedCalendarDate("");
    setSelectedEwayBillFile(null);
    setBookingSelectionConfirmed(false);
    setConfirmedBookingQuantity(0);
    setApprovedAsnRows([]);
    setSelectedApprovedAsnIds([]);
    setAppointmentTabIndex(0);
    resetMultiAsnSelectionState();
  };

  const handleAppointmentTabChange = (event, newValue) => {
    setAppointmentTabIndex(newValue);
  };

  /* ===== STEP BASED APPOINTMENT FLOW ===== */
  // Used by the new "Continue" button on Step 1. Validates the selected date,
  // then advances to Step 2 (Select PO & Materials).
  const handleContinueToMaterialsStep = () => {
    if (!selectedCalendarDate) {
      showAlert("Please select an available appointment date.", {
        icon: "warning",
        title: "Select Date",
      });
      return;
    }
    setAppointmentTabIndex(1);
  };

  const handleCloseBookingPreviewPopup = () => {
    setOpenBookingPreviewPopup(false);
    setBookingPreviewLoading(false);
    if (selectedAppointmentData) {
      setOpenAppointmentPopup(true);
    }
  };

  const handleCalendarMonthChange = (direction) => {
    setCurrentCalendarMonth((prev) => {
      const nextMonth = new Date(prev.getFullYear(), prev.getMonth() + direction, 1);
      return isCurrentOrFutureMonth(nextMonth) ? nextMonth : prev;
    });
  };

  const getSelectedAppointmentQuantity = (row) => {
    if (!row?.items?.length) {
      return 0;
    }

    return row.items.reduce((total, item) => total + Number(item.quantity || 0), 0);
  };

  const getSlotStatus = (dateKey) => {
    if (isPastDate(dateKey)) {
      return "Closed";
    }

    const slot = appointmentDateSlotMap[dateKey];

    if (!slot) {
      return "No Slot Available";
    }

    return Number(slot.quantityPercentExtra || 0) > 0 ? "Available" : "Fully Booked";
  };

  const isSlotDateAvailable = (dateKey) => getSlotStatus(dateKey) === "Available";

  const handleAppointmentDateSelect = (dateKey) => {
    if (!isSlotDateAvailable(dateKey)) {
      return;
    }

    // Selecting a date no longer opens the separate Booking Preview popup.
    // Instead it simply enables the "Select ASN & Materials" step, which now
    // acts as the booking selection screen itself.
    /* ===== STEP BASED APPOINTMENT FLOW ===== */
    // Date selection no longer auto-advances the step; the user must click
    // the "Continue" button on Step 1 to move to Step 2.
    setSelectedCalendarDate(dateKey);
    setBookingSelectionConfirmed(false);
    setConfirmedBookingQuantity(0);

    if (!expandedAccordions.length) {
      const [firstEligibleRow] = getEligibleAppointmentAsnRows();
      if (firstEligibleRow) {
        setExpandedAccordions([getAsnKey(firstEligibleRow)]);
        ensureApprovedMaterialsLoaded(firstEligibleRow);
      }
    }
  };

  const handleEwayBillFileChange = (file) => {
    setSelectedEwayBillFile(file || null);
  };

  const handleApprovedAsnSelectionChange = (rowId) => {
    setSelectedApprovedAsnIds((prev) =>
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]
    );
  };

  const handleSelectAllApprovedAsn = () => {
    const allRowIds = approvedAsnRows.map((row) => row.id);
    const areAllRowsSelected =
      allRowIds.length > 0 && allRowIds.every((id) => selectedApprovedAsnIds.includes(id));

    setSelectedApprovedAsnIds(areAllRowsSelected ? [] : allRowIds);
  };

  const getSelectedApprovedAsnQuantity = () =>
    approvedAsnRows
      .filter((item) => selectedApprovedAsnIds.includes(item.id))
      .reduce((total, item) => total + Number(item.quantity || 0), 0);

  // ================= Multi ASN Appointment Booking helpers =================

  const getAsnKey = (row) => `${row?.vendorCode || ""}-${row?.poNumber || ""}`;

  const getEligibleAppointmentAsnRows = () =>
    generateRows(appointmentAsnStatus).filter(
      (row) => row.approve === "Accepted" || row.approve === "Partially Approved"
    );

  const getAsnRowTotalQuantity = (row) =>
    (row?.items || []).reduce((total, item) => total + Number(item.quantity || 0), 0);

  const ensureApprovedMaterialsLoaded = async (row) => {
    const asnKey = getAsnKey(row);

    if (approvedMaterialCache[asnKey] || approvedMaterialLoadingMap[asnKey]) {
      return;
    }

    try {
      setApprovedMaterialLoadingMap((prev) => ({ ...prev, [asnKey]: true }));
      const response = await fetchVendorAsnApprovedList(row.vendorCode, row.poNumber);

      if (response?.success === "true") {
        setApprovedMaterialCache((prev) => ({
          ...prev,
          [asnKey]: mapApprovedAsnRows(response.ASNStatusList || []),
        }));
      } else {
        setApprovedMaterialCache((prev) => ({ ...prev, [asnKey]: [] }));
      }
    } catch (error) {
      setApprovedMaterialCache((prev) => ({ ...prev, [asnKey]: [] }));
    } finally {
      setApprovedMaterialLoadingMap((prev) => ({ ...prev, [asnKey]: false }));
    }
  };

  const handleToggleAccordionExpand = async (row) => {
    const asnKey = getAsnKey(row);
    // Left panel is a single-select list: clicking a PO makes it the active
    // PO whose materials are shown in the right-hand panel.
    setExpandedAccordions([asnKey]);
    await ensureApprovedMaterialsLoaded(row);
  };

  useEffect(() => {
    if (openAppointmentPopup && selectedCalendarDate && appointmentTabIndex === 1) {
      getEligibleAppointmentAsnRows().forEach((row) => {
        ensureApprovedMaterialsLoaded(row);
      });
    }
  }, [openAppointmentPopup, selectedCalendarDate, appointmentTabIndex]);

  const handleToggleSelectEntireAsn = (row) => {
    const asnKey = getAsnKey(row);
    const materials = approvedMaterialCache[asnKey] || [];
    const nextSelected = !selectedASNMap[asnKey];

    setSelectedASNMap((prev) => ({ ...prev, [asnKey]: nextSelected }));

    setMaterialSelectionMap((prev) => {
      const nextMaterialMap = {};
      materials.forEach((material) => {
        nextMaterialMap[material.id] = nextSelected;
      });
      return { ...prev, [asnKey]: nextMaterialMap };
    });
  };

  const handleToggleMaterialSelection = (row, materialId) => {
    const asnKey = getAsnKey(row);
    const materials = approvedMaterialCache[asnKey] || [];

    setMaterialSelectionMap((prev) => {
      const currentMap = { ...(prev[asnKey] || {}) };
      currentMap[materialId] = !currentMap[materialId];

      const allSelected =
        materials.length > 0 && materials.every((material) => currentMap[material.id]);

      setSelectedASNMap((prevAsnMap) => ({ ...prevAsnMap, [asnKey]: allSelected }));

      return { ...prev, [asnKey]: currentMap };
    });
  };

  const getSelectedMaterialsForAsn = (row) => {
    const asnKey = getAsnKey(row);
    const materials = approvedMaterialCache[asnKey] || [];
    const selectionMap = materialSelectionMap[asnKey] || {};
    return materials.filter((material) => selectionMap[material.id]);
  };

  const getMultiSelectedTotalQuantity = () =>
    Object.keys(materialSelectionMap).reduce((total, asnKey) => {
      const materials = approvedMaterialCache[asnKey] || [];
      const selectionMap = materialSelectionMap[asnKey] || {};
      const asnTotal = materials
        .filter((material) => selectionMap[material.id])
        .reduce((sum, material) => sum + Number(material.quantity || 0), 0);
      return total + asnTotal;
    }, 0);

  const resetMultiAsnSelectionState = () => {
    setSelectedASNMap({});
    setMaterialSelectionMap({});
    setApprovedMaterialCache({});
    setApprovedMaterialLoadingMap({});
    setExpandedAccordions([]);
  };

  const handleCreateAppointmentClick = async () => {
    setIsMultiAsnMode(true);
    setSelectedAppointmentData(null);
    setSelectedCalendarDate("");
    setSelectedEwayBillFile(null);
    setBookingSelectionConfirmed(false);
    setConfirmedBookingQuantity(0);
    setApprovedAsnRows([]);
    setSelectedApprovedAsnIds([]);
    setAppointmentTabIndex(0);
    resetMultiAsnSelectionState();
    setCurrentCalendarMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

    try {
      setLoading(true);
      await Promise.all([getAppointmentDateSlots(), getAppointmentAsnList()]);
    } finally {
      setLoading(false);
      setOpenAppointmentPopup(true);
    }
  };

  const handleMultiAppointmentSubmit = () => {
    if (!selectedCalendarDate) {
      showAlert("Please select an available appointment date.", {
        icon: "warning",
        title: "Select Date",
      });
      return;
    }

    const eligibleRows = getEligibleAppointmentAsnRows();
    const selectedRows = eligibleRows.filter((row) => {
      const key = getAsnKey(row);
      const selection = getSelectedMaterialsForAsnKey(key);
      return selection.length > 0;
    });

    if (!selectedRows.length) {
      showAlert("Please select at least one material to proceed.", {
        icon: "warning",
        title: "No Materials Selected",
      });
      return;
    }

    const totalQuantity = getMultiSelectedTotalQuantity();
    const availableQuantity = Number(selectedDateSlotDetailsForSubmit()?.quantityPercentExtra || 0);

    if (availableQuantity && totalQuantity > availableQuantity) {
      showAlert("Total selected quantity exceeds available quantity for the selected date.", {
        icon: "warning",
        title: "Quantity Exceeded",
      });
      return;
    }

    /* ===== UPDATED REQUEST PAYLOAD ===== */
    const poList = selectedRows.map((row) => {
      const key = getAsnKey(row);
      const selectedMaterials = getSelectedMaterialsForAsnKey(key);

      return {
        vendorPoId: Number(row.id || 0),
        poNumber: String(row.poNumber || ""),
        materials: selectedMaterials.map((item) => ({
          asnId: item.id || "",
          materialCode: item.materialCode || "",
          boxNumber: item.boxNumber || "",
          brand: item.brand || "",
          product: item.product || "",
          materialDesc: item.materialDesc || "",
          barcode: item.barcode || "",
          quantity: Number(item.quantity || 0),
        })),
      };
    });

    const formData = new FormData();
    formData.append("vendor_code", String(vendorCode || ""));
    formData.append("appointmentDate", selectedCalendarDate);
    formData.append("vendor_dispatch_quantity", String(totalQuantity || 0));
    formData.append(
      "poList",
      JSON.stringify(poList)
    );

    if (selectedEwayBillFile) {
      formData.append("ewayBill", selectedEwayBillFile);
    }

    submitAsnAppointment(formData);
  };

  const getSelectedMaterialsForAsnKey = (asnKey) => {
    const materials = approvedMaterialCache[asnKey] || [];
    const selectionMap = materialSelectionMap[asnKey] || {};
    return materials.filter((material) => selectionMap[material.id]);
  };

  const selectedDateSlotDetailsForSubmit = () => appointmentDateSlotMap[selectedCalendarDate];

  const renderCompactTooltipCell = (value, align = "left") => {
    const displayValue = value ?? "-";

    return (
      <Tooltip title={String(displayValue)}>
        <MDTypography
          variant="caption"
          sx={{
            display: "block",
            width: "100%",
            minWidth: 0,
            lineHeight: 1.2,
            textAlign: align,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            cursor: "default",
          }}
        >
          {displayValue}
        </MDTypography>
      </Tooltip>
    );
  };

  const renderWrappedTableCell = (value, width, align = "left") => {
    const displayValue = value ?? "-";

    return (
      <MDTypography
        variant="caption"
        sx={{
          display: "block",
          width,
          maxWidth: width,
          minWidth: width,
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
          lineHeight: 1.45,
          textAlign: align,
        }}
      >
        {displayValue}
      </MDTypography>
    );
  };

  const hasAvailableAppointmentSlots = Object.values(appointmentDateSlotMap).some(
    (slot) => Number(slot?.quantityPercentExtra || 0) > 0
  );

  const getApprovedAsnAvailability = (row) => {
    const mapKey = `${row?.vendorCode}-${row?.poNumber}`;

    return (
      approvedAsnAvailabilityMap[mapKey] || {
        hasMaterialDetails: false,
        isChecking: true,
        message: "Checking material details...",
      }
    );
  };

  const canEditBillDetails = (row) =>
    row?.approve === "Accepted" && Array.isArray(row?.items)
      ? row.items.some((item) => isAppointmentTakenEnabled(item))
      : false;

  const canViewAppointmentBoxes = (row) =>
    Array.isArray(row?.items) ? row.items.some((item) => isAppointmentTakenEnabled(item)) : false;

  const appointmentListColumns = [
    {
      accessor: "poNumber",
      Header: "PO Number",
      width: "80px",
      Cell: ({ value }) => renderWrappedTableCell(value, "80px"),
    },
    {
      accessor: "appointmentDate",
      Header: "Appointment Date",
      width: "80px",
      Cell: ({ value }) => renderWrappedTableCell(value, "80px"),
    },
    {
      accessor: "boxNumber",
      Header: "Box Number",
      width: "70px",
      Cell: ({ value }) => renderWrappedTableCell(value, "70px"),
    },
    {
      accessor: "materialCode",
      Header: "Material Code",
      width: "180px",
      Cell: ({ value }) => renderWrappedTableCell(value, "180px"),
    },
    {
      accessor: "brand",
      Header: "Brand",
      width: "90px",
      Cell: ({ value }) => renderWrappedTableCell(value, "90px"),
    },
    {
      accessor: "product",
      Header: "Product",
      width: "100px",
      Cell: ({ value }) => renderWrappedTableCell(value, "100px"),
    },
    {
      accessor: "materialDesc",
      Header: "Material Desc",
      width: "190px",
      Cell: ({ value }) => renderWrappedTableCell(value, "190px"),
    },
    {
      accessor: "barcode",
      Header: "Barcode",
      width: "80px",
      Cell: ({ value }) => renderWrappedTableCell(value, "80px"),
    },
    {
      accessor: "quantity",
      Header: "Quantity",
      width: "80px",
      align: "right",
      Cell: ({ value }) => renderWrappedTableCell(value, "80px", "right"),
    },
  ];

  const getAppointmentListRows = (row) => {
    if (!Array.isArray(row?.items)) {
      return [];
    }

    return row.items
      .filter((item) => isAppointmentTakenEnabled(item))
      .map((item) => ({
        id: item.id,
        poNumber: item.poNumber || row.poNumber || "-",
        appointmentDate: formatDateValue(getAppointmentDate(item)) || "-",
        boxNumber: item.boxNumber || "-",
        materialCode: item.materialCode || "-",
        brand: item.brand || "-",
        product: item.product || "-",
        materialDesc: item.materialDesc || "-",
        barcode: item.barcode || "-",
        quantity: item.quantity || 0,
        appointmentDate: formatDateValue(getAppointmentDate(item)) || "-",
      }));
  };

  const handleAppointmentListView = (row) => {
    const appointmentRows = getAppointmentListRows(row);

    if (!appointmentRows.length) {
      showAlert("No appointment data found for this PO.", {
        icon: "info",
        title: "No Appointment Data",
      });
      return;
    }

    setSelectedAppointmentListData({
      poNumber: row?.poNumber || "-",
      rows: appointmentRows,
    });
    setOpenAppointmentListPopup(true);
  };

  const handleCloseAppointmentListPopup = () => {
    setOpenAppointmentListPopup(false);
    setSelectedAppointmentListData(null);
  };

  const approvedMaterialColumns = [
    {
      accessor: "select",
      Header: () => {
        const allRowsSelected =
          approvedAsnRows.length > 0 &&
          approvedAsnRows.every((row) => selectedApprovedAsnIds.includes(row.id));

        return (
          <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={allRowsSelected}
              onChange={handleSelectAllApprovedAsn}
            />
            Select All
          </label>
        );
      },
      width: 95,
      align: "center",
      // eslint-disable-next-line react/prop-types
      Cell: (cellProps) => {
        // eslint-disable-next-line react/prop-types
        const rowId = cellProps?.row?.original?.id;

        return (
          <input
            type="checkbox"
            checked={selectedApprovedAsnIds.includes(rowId)}
            onChange={() => handleApprovedAsnSelectionChange(rowId)}
          />
        );
      },
    },
    {
      accessor: "materialCode",
      Header: "Material Code",
      width: 110,
      Cell: ({ value }) => renderCompactTooltipCell(value),
    },
    {
      accessor: "boxNumber",
      Header: "Box Number",
      width: 90,
      Cell: ({ value }) => renderCompactTooltipCell(value),
    },
    {
      accessor: "brand",
      Header: "Brand",
      width: 100,
      Cell: ({ value }) => renderCompactTooltipCell(value),
    },
    {
      accessor: "product",
      Header: "Product",
      width: 100,
      Cell: ({ value }) => renderCompactTooltipCell(value),
    },
    // { accessor: "invoiceNumber", Header: "Invoice Number" },
    // { accessor: "invoiceDate", Header: "Invoice Date" },
    {
      accessor: "materialDesc",
      Header: "Material Desc",
      width: 170,
      Cell: ({ value }) => renderCompactTooltipCell(value),
    },
    {
      accessor: "barcode",
      Header: "Barcode",
      width: 130,
      Cell: ({ value }) => renderCompactTooltipCell(value),
    },
    {
      accessor: "quantity",
      Header: "Quantity",
      width: 80,
      align: "center",
      Cell: ({ value }) => renderCompactTooltipCell(value, "center"),
    },
  ];

  const mapApprovedAsnRows = (dataList = []) =>
    dataList.map((item) => ({
      id: item.id,
      select: "",
      materialCode: item.materialCode || "-",
      boxNumber: item.boxNumber || "-",
      materialDesc: item.materialDesc || "-",
      brand: item.brand || "-",
      product: item.product || "-",
      invoiceNumber: item.InvoiceNumber || "-",
      invoiceDate: item.InvoiceDate || "-",
      barcode: item.barcode || "-",
      quantity: item.quantity || 0,
    }));

  const handleOpenBookingPreview = async (dateKey = selectedCalendarDate) => {
    if (!dateKey) {
      showAlert("Please select an available appointment date.", {
        icon: "warning",
        title: "Select Date",
      });
      return;
    }

    if (!selectedAppointmentData?.poNumber || !selectedAppointmentData?.vendorCode) {
      showAlert("PO number or vendor code is missing.", {
        icon: "warning",
        title: "Unable to load details",
      });
      return;
    }

    try {
      setBookingPreviewLoading(true);
      setLoading(true);
      const response = await fetchVendorAsnApprovedList(
        selectedAppointmentData.vendorCode,
        selectedAppointmentData.poNumber
      );

      if (response?.success === "true") {
        const mappedRows = mapApprovedAsnRows(response.ASNStatusList || []);
        setApprovedAsnRows(mappedRows);
        setSelectedApprovedAsnIds([]);
        setSelectedCalendarDate(dateKey);
        setOpenAppointmentPopup(false);
        setOpenBookingPreviewPopup(true);
      } else {
        setApprovedAsnRows([]);
        showAlert(response?.msg || "No approved ASN details found.", {
          icon: "info",
          title: "No Data",
        });
      }
    } catch (error) {
      setApprovedAsnRows([]);
      showAlert("Unable to fetch approved ASN details.", {
        icon: "error",
        title: "Error",
      });
    } finally {
      setBookingPreviewLoading(false);
      setLoading(false);
    }
  };

  const handleBookingPreviewNext = () => {
    setConfirmedBookingQuantity(getSelectedApprovedAsnQuantity());
    setBookingSelectionConfirmed(true);
    setOpenBookingPreviewPopup(false);
    setOpenAppointmentPopup(true);
  };

  const handleAppointmentSubmit = () => {
    const selectedAppointmentRows = approvedAsnRows.filter((item) =>
      selectedApprovedAsnIds.includes(item.id)
    );

    if (!selectedCalendarDate) {
      showAlert("Please select an available appointment date.", {
        icon: "warning",
        title: "Select Date",
      });
      return;
    }

    if (!bookingSelectionConfirmed) {
      showAlert("Please click a date and complete the confirm booking popup first.", {
        icon: "warning",
        title: "Selection Pending",
      });
      return;
    }

    if (!selectedAppointmentRows.length) {
      showAlert("Please select at least one row in confirm booking.", {
        icon: "warning",
        title: "No Rows Selected",
      });
      return;
    }

    const appointmentDetailsPayload = {
      asnAppointmentDetails: selectedAppointmentRows.map((item) => ({
        id: item.id || "",
        materialCode: item.materialCode || "",
        boxNumber: item.boxNumber || "",
        brand: item.brand || "",
        product: item.product || "",
        materialDesc: item.materialDesc || "",
        barcode: item.barcode || "",
        quantity: String(item.quantity || 0),
      })),
    };

    const formData = new FormData();
    formData.append("vendor_code", String(selectedAppointmentData?.vendorCode || vendorCode || ""));
    formData.append("vendorPoId", String(selectedAppointmentData?.id || ""));
    formData.append("poNumber", String(selectedAppointmentData?.poNumber || ""));
    formData.append("appointmentDate", selectedCalendarDate);
    formData.append("vendor_dispatch_quantity", String(confirmedBookingQuantity || 0));
    formData.append(
      "asnAppointmentDetailsListArray",
      JSON.stringify(appointmentDetailsPayload)
    );

    if (selectedEwayBillFile) {
      formData.append("ewayBill", selectedEwayBillFile);
    }

    submitAsnAppointment(formData);
  };

  const submitAsnAppointment = async (formData) => {
    try {
      setLoading(true);
      const response = await createAsnAppointmentAPI(formData);

      if (response?.success === "true") {
        await Promise.all([getVendorStatus(), getAppointmentDateSlots(), getAppointmentAsnList()]);
        showAlert(response?.msg || "ASN Appointment created successfully", {
          icon: "success",
          title: "Appointment Created",
        });
        handleCloseAppointmentPopup();
      } else {
        showAlert(response?.msg || "Unable to create ASN appointment.", {
          icon: "warning",
          title: "Validation",
        });
      }
    } catch (error) {
      showAlert(error?.msg || error?.message || "Unable to create ASN appointment.", {
        icon: "error",
        title: "Submission Failed",
      });
    } finally {
      setLoading(false);
    }
  };

  const expandAppointmentDateSlots = (slotList = []) => {
    const expandedSlots = {};

    slotList.forEach((slot) => {

      if (slot?.datesMaster) {
        const normalizedDate = normalizeDateKey(slot.datesMaster);

        if (!normalizedDate) {
          return;
        }

        expandedSlots[normalizedDate] = {
          ...slot,
          quantity: Number(slot.quantity || 0),
          quantityPercentExtra: Number(slot.quantityPercentExtra || 0),
          dateKey: normalizedDate,
        };
        return;
      }

      if (slot?.fromDate && slot?.toDate) {
        const startDate = new Date(slot.fromDate);
        const endDate = new Date(slot.toDate);

        if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
          return;
        }

        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
          const dateKey = normalizeDateKey(currentDate);
          expandedSlots[dateKey] = {
            ...slot,
            quantity: Number(slot.quantity || 0),
            quantityPercentExtra: Number(slot.quantityPercentExtra || 0),
            dateKey,
          };
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }
    });

    return expandedSlots;
  };

  const getAppointmentDateSlots = async () => {
    try {
      const response = await fetchAppointmentDateQuantityMasterList();

      if (response?.success === "true") {
        const expandedSlots = expandAppointmentDateSlots(
          response.appointmentDateQntyMasterList || []
        );

        setAppointmentDateSlotMap(expandedSlots);
      } else {
        console.warn("Appointment slots API returned an unsuccessful response:", response);
        setAppointmentDateSlotMap({});
      }
    } catch (error) {
      console.error("Failed to fetch appointment date slots:", error);
      setAppointmentDateSlotMap({});
    }
  };

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      // flex: 1,
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          {/* Download */}
          <Tooltip title="Download">
            <IconButton
              size="small"
              color="success"
              onClick={() => handleDownloadClick(params.row)}
            >
              <DownloadIcon fontSize="medium" />
            </IconButton>
          </Tooltip>

          {/* View */}
          <Tooltip title="View">
            <IconButton size="small" color="info" onClick={() => handleViewClick(params.row)}>
              <RemoveRedEye fontSize="medium" />
            </IconButton>
          </Tooltip>

          {/* Delete */}
          <Tooltip title="Delete ASN">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.row)}
            >
              <Delete />
            </IconButton>
          </Tooltip>

          {/* Edit (Only if Accepted and appointment taken) */}
          {canEditBillDetails(params.row) && (
            Number(params.row.isBilledDetailsFilled) === 1 ? (
              <Tooltip title="View Bill Details">
                <IconButton
                  size="small"
                  color="warning"
                  onClick={() => handleViewBillDetailsClick(params.row)}
                >
                  <RemoveRedEye fontSize="medium" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Edit Bill Details">
                <IconButton
                  size="small"
                  color="warning"
                  onClick={() => handleEditClick(params.row)}
                >
                  <Edit fontSize="medium" />
                </IconButton>
              </Tooltip>
            )
          )}
        </>
      ),
    },
    {
      field: "poNumber",
      headerName: "PO Number",
      flex: 1,
    },
    {
      field: "poDate",
      headerName: "PO Date",
      flex: 1,
      sortable: true,
      filterable: true,

      valueGetter: (params) => parseGridDateValue(params.row.poDate),
      renderCell: (params) => params.row.poDate || "-",
      valueFormatter: (params) => params.row?.poDate || "",
    },
    {
      field: "city",
      headerName: "City",
      flex: 1,
      type: "string",
      valueGetter: (params) => params.row.city || "",
      sortable: true,
      filterable: true,
    },
    {
      field: "appointmentDate",
      headerName: "Appointment",
      flex: 0.9,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const isAccepted = params.row.approve === "Accepted";
        const approvedAsnAvailability = getApprovedAsnAvailability(params.row);
        const hasMaterialDetails = approvedAsnAvailability.hasMaterialDetails;
        const isCheckingMaterialDetails =
          approvedAsnAvailability.isChecking || approvedAsnAvailabilityLoading;
        const isDisabled =
          !isAccepted ||
          !hasAvailableAppointmentSlots ||
          isCheckingMaterialDetails ||
          !hasMaterialDetails;

        return (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {/* <Tooltip
              title={
                !isAccepted
                  ? "Appointment available after approval"
                  : !hasAvailableAppointmentSlots
                    ? "No appointment slots available"
                    : isCheckingMaterialDetails
                      ? "Checking material details..."
                      : !hasMaterialDetails
                        ? approvedAsnAvailability.message || "No material details found"
                        : params.row.appointmentDate
                          ? "View Appointment Calendar"
                          : "Book Appointment"
              }
            >
              <span>
                <IconButton
                  size="small"
                  color="warning"
                  disabled={isDisabled}
                  onClick={() => openAppointmentModal(params.row)}
                >
                  <CalendarMonth fontSize="small" />
                </IconButton>
              </span>
            </Tooltip> */}

            <Tooltip
              title={
                canViewAppointmentBoxes(params.row)
                  ? "View Appointment Data"
                  : "No appointment data available"
              }
            >
              <span>
                <IconButton
                  size="small"
                  color="info"
                  disabled={!canViewAppointmentBoxes(params.row)}
                  onClick={() => handleAppointmentListView(params.row)}
                >
                  <RemoveRedEye fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        );
      },
    },
    {
      field: "documents",
      headerName: "Documents",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const documents = params.row.documents || [];

        if (!documents.length) {
          return <span>-</span>;
        }

        return (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {documents.map((document) => (
              <Tooltip key={document.label} title={document.label}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleOpenDocument(document)}
                  >
                    {document.iconName === "fact_check" ? (
                      <FactCheck fontSize="small" />
                    ) : document.iconName === "inventory" ? (
                      <Inventory2 fontSize="small" />
                    ) : (
                      <ReceiptLong fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            ))}
          </div>
        );
      },
    },

    {
      field: "approve",
      headerName: "Status",
      flex: 1.2,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const status = params.value;

        // const color = status === "Accepted" ? "green" : status === "Rejected" ? "red" : "#ffaa00"; // Pending / Others

        const color =
          status === "Accepted"
            ? "green"
            : status === "Rejected"
              ? "red"
              : status === "Partially Approved"
                ? "#1976d2"   // Blue
                : "#ffaa00";

        return (
          <span
            style={{
              padding: "4px 10px",
              borderRadius: "12px",
              color: "white",
              fontFamily: "bold",
              backgroundColor: color,
              fontSize: "12px",
            }}
          >
            {status}
          </span>
        );
      },
    },

    {
      field: "remark",
      headerName: "Remark",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title={params.row.items?.[0]?.remark || ""}>
          <MDTypography
            variant="light"
            noWrap
            sx={{
              display: "inline-block",
              maxWidth: 150,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {params.row.items?.[0]?.remark}
          </MDTypography>
        </Tooltip>
      ),
    },
    // {
    //   field: "actions",
    //   headerName: "Actions",
    //   width: 150,
    //   sortable: false,
    //   filterable: false,
    //   renderCell: (params) => (
    //     <>
    //       {/* View Details */}
    //       <Tooltip title="View">
    //         <IconButton size="small" color="info" onClick={() => handleViewClick(params.row)}>
    //           <RemoveRedEye fontSize="medium" />
    //         </IconButton>
    //       </Tooltip>

    //       {/* Edit Bill Details */}
    //       {params.row.approve === "Accepted" && (
    //         <Tooltip title="Edit Bill Details">
    //           <IconButton size="small" color="warning" onClick={() => handleEditClick(params.row)}>
    //             <Edit fontSize="medium" />
    //           </IconButton>
    //         </Tooltip>
    //       )}
    //     </>
    //   ),
    // },
  ];

  const materialcolumns = [
    {
      Header: "Actions",
      accessor: "delete",
      width: 80,

      Cell: (cell) => {
        const { original } = cell.row;

        // Show action only on first row of each box
        if (!original.isFirstBoxRow) return null;

        // Show nothing if box is not Pending
        if (String(original.approve) !== "0") {
          return null;
        }

        // Show delete only if appointment is NOT taken
        if (String(original.isAppointmentTaken).toLowerCase() !== "no") {
          return null;
        }

        return (
          <Tooltip title="Delete Box">
            <IconButton
              color="error"
              size="small"
              disabled={deleteLoading}
              onClick={() => handleOpenDeleteBoxPopup(original)}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
    { accessor: "materialCode", Header: "Material Code", hidden: true },
    { accessor: "boxNumber", Header: "Box Number", hidden: true },
    { accessor: "brand", Header: "Brand", hidden: true },
    { accessor: "product", Header: "Product", hidden: true },
    // { accessor: "invoiceNumber", Header: "Invoice Number", hidden: true },
    // { accessor: "invoiceDate", Header: "Invoice Date", hidden: true },
    { accessor: "materialDesc", Header: "Material Desc", hidden: true },
    { accessor: "barcode", Header: "Barcode", hidden: true },
    { accessor: "quantity", Header: "quantity", hidden: true },
  ];

  // const generateMaterialRows = (dataList) => {
  //   if (!selectedData) return [];

  //   return dataList
  //     .filter(
  //       (data) =>
  //         data.poNumber === selectedData.poNumber &&
  //         data.vendorCode === selectedData.vendorCode
  //     )
  //     .map((data) => ({
  //       id: data.id,
  //       materialCode: data.materialCode,
  //       boxNumber: data.boxNumber,
  //       materialDesc: data.materialDesc,
  //       brand: data.brand,
  //       product: data.product,
  //       invoiceNumber: data.InvoiceNumber,
  //       invoiceDate: data.InvoiceDate,
  //       barcode: data.barcode,
  //       quantity: data.quantity,
  //     }));
  // };

  const generateMaterialRows = (dataList) => {
    if (!selectedData) return [];

    // Get all box-level records for this ASN
    const asnItems = dataList.filter(
      (data) =>
        data.poNumber === selectedData.poNumber &&
        data.vendorCode === selectedData.vendorCode
    );

    // Check mixed status
    const hasRejected = asnItems.some((item) => item.approve === "2");
    const hasAcceptedOrPending = asnItems.some(
      (item) => item.approve === "1" || item.approve === "0"
    );
    const mixedWithRejected = hasRejected && hasAcceptedOrPending;

    // NEW: Track first occurrence of each box number
    const firstOccurrence = {};

    return asnItems
      .filter((data) => {
        if (selectedData.approve === "Partially Approved") {
          return data.approve === "0";
        }

        if (mixedWithRejected) {
          return data.approve === "1" || data.approve === "0";
        }

        return true;
      })
      .map((data) => {
        const isFirst = !firstOccurrence[data.boxNumber];
        firstOccurrence[data.boxNumber] = true;

        return {
          id: data.id,
          materialCode: data.materialCode,
          boxNumber: data.boxNumber,
          brand: data.brand,
          product: data.product,
          invoiceNumber: data.InvoiceNumber,
          invoiceDate: data.InvoiceDate,
          materialDesc: data.materialDesc,
          barcode: data.barcode,
          quantity: data.quantity,
          poNumber: data.poNumber,
          vendorCode: data.vendorCode,
          isAppointmentTaken: data.isAppointmentTaken,
          approve: data.approve,

          // NEW
          isFirstBoxRow: isFirst,
        };
      });
  };

  const handleDownloadClick = async (row) => {
    const materials = asnStatus.filter((item) => item.poNumber === row.poNumber);

    // Combined Header
    const sheetHeader = [
      "PO Number",
      "PO Date",
      "City",
      "Material Code",
      "Brand",
      "Product",
      "Material Desc",
      "Barcode",
      "Quantity",
    ];

    // Combined Data Rows
    const sheetRows = materials.map((item) => [
      row.poNumber,
      row.poDate,
      row.city,
      item.materialCode,
      item.brand,
      item.product,
      item.materialDesc,
      item.barcode,
      item.quantity,
    ]);

    const sheetData = [sheetHeader, ...sheetRows];

    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    // Style the header row
    for (let C = 0; C < sheetHeader.length; C++) {
      const cell = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
      if (cell) {
        cell.s = {
          font: { bold: true, color: { rgb: "ffffff" } },
          fill: { fgColor: { rgb: "4CAF50" } },
          alignment: { horizontal: "center" },
        };
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ASN Report");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array", cellStyles: true });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, `ASN_Report_${row.poNumber}.xlsx`);
  };

  const handleViewClick = (row) => {
    setSelectedData(row);
    setOpenPopup(true);
  };

  const generateRows = (dataList) => {
    const grouped = {};

    dataList.forEach(async (data) => {
      const groupKey = `${data.poNumber}-${data.vendorCode}`;
      if (!grouped[groupKey]) {
        //  const docs = buildDocumentLinks(data.poNumber, data.vendorCode);

        //  const validDocs = await filterValidDocuments(docs); 
        // console.log(validDocs);
        grouped[groupKey] = {
          id: data.id,
          poNumber: data.poNumber,
          poDate: data.poDate,
          city: data.city,
          vendorCode: data.vendorCode,
          appointmentDate: getAppointmentDate(data),
          isAppointmentTaken: data.isAppointmentTaken,
          isBilledDetailsFilled: data.isBilledDetailsFilled,
          documents: buildDocumentLinks(data.poNumber, data.vendorCode),
          approve: data.approve == "1" ? "Accepted" : data.approve == "2" ? "Rejected" : "Pending",
          createdAt: data.createdAt,
          items: [],
        };
      }
      if (!grouped[groupKey].appointmentDate) {
        grouped[groupKey].appointmentDate = getAppointmentDate(data);
      }

      if (!grouped[groupKey].documents?.length) {
        grouped[groupKey].documents = buildDocumentLinks(data.poNumber, data.vendorCode, data);
      }

      // Add individual row to the group (if you want to retain detail list)
      grouped[groupKey].items.push(data);



      const statuses = grouped[groupKey].items.map(item => item.approve);

      const uniqueStatuses = [...new Set(statuses)];

      if (uniqueStatuses.length > 1) {
        grouped[groupKey].approve = "Partially Approved";
      } else {
        grouped[groupKey].approve =
          uniqueStatuses[0] === "1"
            ? "Accepted"
            : uniqueStatuses[0] === "2"
              ? "Rejected"
              : "Pending";
      }
    });

    // Return as array
    return Object.values(grouped);
  };

  const handleSelectionChange = (newSelection) => {
    setSelectedRows(newSelection);
  };

  const handleClosePopup = () => {
    setOpenPopup(false);
    setSelectedData(null);
  };

  // Edit modal
  const handleEditClick = async (row) => {
    // MUI DataGrid passes the row data directly as `params.row`.
    // Some table libs pass `row.original`; accept both.
    const poNumberValue = row?.original?.poNumber ?? row?.poNumber;
    if (!poNumberValue) {
      return; // nothing to edit
    }
    const poNumber = String(poNumberValue);
    setIsBillDetailsViewMode(false);
    setIsBillDetailsUpdateMode(row?.isBilledDetailsFilled === true);
    setEditPONumber(poNumber);
    const hasEligibleRows = await getEditData(poNumber);

    if (hasEligibleRows) {
      setOpenEditPopup(true);
    } else {
      showAlert("Only appointment taken = yes box numbers are available for edit.", {
        icon: "info",
        title: "No Eligible Box Number",
      });
    }
  };

  const handleViewBillDetailsClick = async (row) => {
    const poNumberValue = row?.original?.poNumber ?? row?.poNumber;
    if (!poNumberValue) {
      return;
    }

    const poNumber = String(poNumberValue);
    setIsBillDetailsViewMode(true);
    setIsBillDetailsUpdateMode(true);
    setEditPONumber(poNumber);
    const hasEligibleRows = await getEditData(poNumber);

    if (hasEligibleRows) {
      setOpenEditPopup(true);
    } else {
      setIsBillDetailsViewMode(false);
      showAlert("Only appointment taken = yes box numbers are available for edit.", {
        icon: "info",
        title: "No Eligible Box Number",
      });
    }
  };

  const handleCloseEditPopup = () => {
    setOpenEditPopup(false);
    setIsBillDetailsViewMode(false);
    setIsBillDetailsUpdateMode(false);
    setEditData([]);
  };

  const handleEditBillDetails = () => {
    setIsBillDetailsViewMode(false);
  };

  // Edit table columns
  const editColumns = [
    { accessor: "poNumber", Header: "PO Number" },
    { accessor: "vendorCode", Header: "Vendor Code" },
    {
      accessor: "billNumber",
      Header: "Invoice Number",
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => {
        return (
          /* eslint-disable react/prop-types */
          <TextField
            defaultValue={
              // eslint-disable-next-line react/prop-types
              billData.bills.find((i) => i.boxNumber == row.original.boxNumber)?.billNumber
            }
            // eslint-disable-next-line react/prop-types
            onBlur={(e) => {
              if (!isBillDetailsViewMode) {
                updateBillNumber(row.original.boxNumber, e.target.value);
              }
            }}
            disabled={isBillDetailsViewMode}
            // eslint-disable-next-line react/prop-types
            error={!!errors[row.original.boxNumber]?.billNumber}
            // eslint-disable-next-line react/prop-types
            helperText={errors[row.original.boxNumber]?.billNumber || ""}
          ></TextField>
        );
      },
    },
    {
      accessor: "invoiceDate",
      Header: "Invoice Date",
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => {
        // eslint-disable-next-line react/prop-types
        return (
          <MDInput
            type="date"
            value={
              billData.bills.find(
                (i) => i.boxNumber == row.original.boxNumber
              )?.invoiceDate || ""
            }
            onChange={(e) => {
              if (!isBillDetailsViewMode) {
                updateInvoiceDateNumber(
                  row.original.boxNumber,
                  e.target.value
                );
              }
            }}
            disabled={isBillDetailsViewMode}
            // eslint-disable-next-line react/prop-types
            // error={!!errors[row.original.boxNumber]?.invoiceDate}
            // eslint-disable-next-line react/prop-types
            helperText={errors[row.original.boxNumber]?.invoiceDate || ""}
            sx={{
              "&.Mui-error": {
                backgroundImage: "none !important",
              },
              "& .MuiInputBase-root": {
                border: !!errors[row.original.boxNumber]?.invoiceDate
                  ? "1px solid red"
                  : "",
                borderRadius: 1,
              },
              "& .MuiFormHelperText-root": {
                color: "red",
              },
            }}
          />
        );
      },
    },
    {
      accessor: "ewayBillNumber",
      Header: "E-Way Bill Number",
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => {
        return (
          // eslint-disable-next-line react/prop-types
          <TextField
            defaultValue={
              // eslint-disable-next-line react/prop-types
              billData.bills.find((i) => i.boxNumber == row.original.boxNumber)?.ewayBillNumber
            }
            // eslint-disable-next-line react/prop-types
            onBlur={(e) => {
              if (!isBillDetailsViewMode) {
                updateEWayBillNumber(row.original.boxNumber, e.target.value);
              }
            }}
            disabled={isBillDetailsViewMode}
            // eslint-disable-next-line react/prop-types
            error={!!errors[row.original.boxNumber]?.ewayBillNumber}
            // eslint-disable-next-line react/prop-types
            helperText={errors[row.original.boxNumber]?.ewayBillNumber || ""}
          ></TextField>
        );
      },
    },
    {
      accessor: "deliveryDate",
      Header: "Delivery Date",
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => {
        // eslint-disable-next-line react/prop-types
        return (
          <MDInput
            type="date"
            InputLabelProps={{ shrink: true }}
            value={
              billData.bills.find(
                (i) => i.boxNumber == row.original.boxNumber
              )?.deliveryDate || ""
            }
            onChange={(e) => {
              if (!isBillDetailsViewMode) {
                updateDeliveryDateNumber(
                  row.original.boxNumber,
                  e.target.value
                );
              }
            }}
            disabled={isBillDetailsViewMode}
            // eslint-disable-next-line react/prop-types
            // error={!!errors[row.original.boxNumber]?.deliveryDate}
            // eslint-disable-next-line react/prop-types
            helperText={errors[row.original.boxNumber]?.deliveryDate || ""}
            sx={{
              "& .MuiInputBase-root": {
                border: !!errors[row.original.boxNumber]?.deliveryDate ? "1px solid red" : "",
                borderRadius: 1,
              },
              "& .MuiFormHelperText-root": {
                color: "red",
              },
            }}
          />
        );
      },
    },
  ];

  // Edit rows
  const getEditData = async (poNumber) => {
    try {
      setLoading(true);
      setEditData([]);
      const response = await fetchBoxNumberByVendor(poNumber, vendorCode); // Using the passed in value
      console.log("EDIT API RESPONSE:", response);

      if (response && response.success === "true") {
        const responseData = Array.isArray(response.data) ? response.data : [response.data];

        console.log("EDIT RESPONSE DATA:", responseData);
        console.log("APPOINTMENT VALUES:", responseData.map(item => ({
          boxNumber: item.boxNumber,
          isAppointmentTaken: item.isAppointmentTaken
        })));

        const eligibleData = responseData.filter((item) => isAppointmentTakenEnabled(item));
        const billKey = `${poNumber}-${vendorCode}`;
        const existingBill = eligibleData.find(
          (item) => item.InvoiceNumber || item.InvoiceDate || item.ewayBillNumber || item.deliveryDate
        );
        const filteredEditData = eligibleData.length
          ? [
            {
              ...existingBill,
              poNumber,
              vendorCode,
              boxNumber: billKey,
            },
          ]
          : [];
        setEditData(filteredEditData);

        const bills = filteredEditData.map((item) => ({
          boxNumber: item.boxNumber,
          boxNumbers: eligibleData
            .map((row) => String(row.boxNumber ?? "").trim())
            .filter((boxNumber, index, values) => boxNumber && values.indexOf(boxNumber) === index),
          billNumber: item.InvoiceNumber,
          invoiceDate: item.InvoiceDate,
          ewayBillNumber: item.ewayBillNumber,
          deliveryDate: item.deliveryDate,
        }));

        const billDetails = {
          vendorCode: vendorCode,
          PONumber: poNumber,
          bills: bills,
        };

        setBillData(billDetails);
        return filteredEditData.length > 0;
      } else {
        setBillData({
          vendorCode: vendorCode,
          PONumber: poNumber,
          bills: [],
        });
      }
      return false;
    } catch (error) {
      setBillData({
        vendorCode: vendorCode,
        PONumber: poNumber,
        bills: [],
      });
      return false;
    } finally {
      setLoading(false);
    }
  };
  // updater e way bill function
  const updateInvoiceDateNumber = (boxNumber, value) => {
    validateField(boxNumber, "invoiceDate", value);

    setBillData((prevData) => {
      if (!prevData) return prevData; // safety check if state is null/undefined

      return {
        ...prevData,
        bills: prevData.bills.map((bill) =>
          bill.boxNumber === String(boxNumber) ? { ...bill, invoiceDate: value } : bill
        ),
      };
    });
  };

  // updater bill function
  const updateBillNumber = (boxNumber, value) => {
    validateField(boxNumber, "billNumber", value);

    setBillData((prevData) => {
      if (!prevData) return prevData; // safety check if state is null/undefined

      return {
        ...prevData,
        bills: prevData.bills.map((bill) =>
          bill.boxNumber === String(boxNumber) ? { ...bill, billNumber: value } : bill
        ),
      };
    });
  };

  // updater e way bill function
  const updateEWayBillNumber = (boxNumber, value) => {
    validateField(boxNumber, "ewayBillNumber", value);

    setBillData((prevData) => {
      if (!prevData) return prevData; // safety check if state is null/undefined

      return {
        ...prevData,
        bills: prevData.bills.map((bill) =>
          bill.boxNumber === String(boxNumber) ? { ...bill, ewayBillNumber: value } : bill
        ),
      };
    });
  };

  // updater e way bill function
  const updateDeliveryDateNumber = (boxNumber, value) => {
    validateField(boxNumber, "deliveryDate", value);

    setBillData((prevData) => {
      if (!prevData) return prevData; // safety check if state is null/undefined

      return {
        ...prevData,
        bills: prevData.bills.map((bill) =>
          bill.boxNumber === String(boxNumber) ? { ...bill, deliveryDate: value } : bill
        ),
      };
    });
  };

  const uploadBillDetails = async () => {
    if (!billData?.bills?.length) {
      showAlert("No eligible box numbers found for update.", {
        icon: "info",
        title: "Nothing To Update",
      });
      return;
    }

    const newErrors = {};
    billData.bills.forEach((bill) => {
      const fieldErrors = {};
      if (!bill.boxNumber) fieldErrors.boxNumber = "Box Number is required";
      if (!bill.billNumber) fieldErrors.billNumber = "Invoice Number is required";
      if (!bill.ewayBillNumber) fieldErrors.ewayBillNumber = "E-Way Bill Number is required";
      if (!bill.deliveryDate) fieldErrors.deliveryDate = "Delivery Date is required";
      if (!bill.invoiceDate) fieldErrors.invoiceDate = "Invoice Date is required";

      newErrors[bill.boxNumber || `temp-${Math.random()}`] = fieldErrors;
    });

    setErrors(newErrors);

    // Check if any field error exists
    const hasErrors = Object.values(newErrors).some((errorObj) =>
      Object.values(errorObj).some((errorMsg) => errorMsg)
    );

    if (hasErrors) {
      return;
    }

    try {
      setLoading(true);
      const uploadData = {
        ...billData,
        bills: billData.bills.flatMap((bill) =>
          bill.boxNumbers.map((boxNumber) => ({
            boxNumber,
            billNumber: bill.billNumber,
            invoiceDate: bill.invoiceDate,
            ewayBillNumber: bill.ewayBillNumber,
            deliveryDate: bill.deliveryDate,
          }))
        ),
      };
      const response = await uploadBillDetailsAPI(uploadData);
      if (response && response.success === true) {
        await getVendorStatus();
        showAlert(
          isBillDetailsUpdateMode
            ? "Bill Details Updated Successfully!"
            : "Bill Details Uploaded Successfully!",
          {
          icon: "success",
            title: isBillDetailsUpdateMode ? "Successfully Updated" : "Successfully Uploaded",
          }
        );
        handleCloseEditPopup();
      } else {
        showAlert("Bill Details Not Uploaded!");
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  return {
    asnStatus,
    cloading: asnStatusLoading,
    paginationModel,
    rowCount,
    setPaginationModel,
    handleSelectionChange,
    generateRows,
    materialcolumns,
    generateMaterialRows,
    columns,
    handleClosePopup,
    openPopup,
    selectedData,
    openAppointmentPopup,
    openBookingPreviewPopup,
    openAppointmentListPopup,
    selectedAppointmentData,
    selectedAppointmentListData,
    handleCloseAppointmentPopup,
    handleCloseAppointmentListPopup,
    handleOpenBookingPreview,
    handleCloseBookingPreviewPopup,
    selectedCalendarDate,
    setSelectedCalendarDate,
    handleAppointmentDateSelect,
    selectedEwayBillFile,
    handleEwayBillFileChange,
    currentCalendarMonth,
    handleCalendarMonthChange,
    getCalendarDays,
    getMonthLabel,
    isPastDate,
    isCurrentOrFutureMonth,
    appointmentCalendarData: buildAppointmentCalendarData(asnStatus),
    appointmentDateSlotMap,
    getSlotStatus,
    isSlotDateAvailable,
    approvedMaterialColumns,
    appointmentListColumns,
    approvedAsnRows,
    bookingPreviewLoading,
    selectedApprovedAsnIds,
    getSelectedApprovedAsnQuantity,
    formatDateValue,
    bookingSelectionConfirmed,
    confirmedBookingQuantity,
    handleBookingPreviewNext,
    handleAppointmentSubmit,

    editData,
    openEditPopup,
    isBillDetailsViewMode,
    handleEditBillDetails,
    handleEditClick,
    handleViewBillDetailsClick,
    handleCloseEditPopup,
    editColumns,
    getEditData,
    uploadBillDetails,

    searchPONumber,
    setSearchPONumber,
    poFilter,
    setPoFilter,
    buildDocumentLinks,

    openDeletePopup,
    deleteData,
    handleDeleteClick,
    handleCloseDeletePopup,
    handleDeleteASN,

    openDeleteBoxPopup,
    selectedDeleteBox,
    handleOpenDeleteBoxPopup,
    handleCloseDeleteBoxPopup,

    // ---- Multi ASN Appointment Booking ----
    isMultiAsnMode,
    handleCreateAppointmentClick,
    getEligibleAppointmentAsnRows,
    getAsnKey,
    getAsnRowTotalQuantity,
    expandedAccordions,
    handleToggleAccordionExpand,
    approvedMaterialCache,
    approvedMaterialLoadingMap,
    selectedASNMap,
    materialSelectionMap,
    handleToggleSelectEntireAsn,
    handleToggleMaterialSelection,
    getSelectedMaterialsForAsn,
    getMultiSelectedTotalQuantity,
    handleMultiAppointmentSubmit,
    appointmentTabIndex,
    handleAppointmentTabChange,
    handleContinueToMaterialsStep, /* ===== STEP BASED APPOINTMENT FLOW ===== */

    deleteLoading,
    handleDeleteBox,
  };
};

export default AsnStatusController;